/**
 * useAddressBookRestore
 *
 * Powers the "Restore address book from server backup" settings action (#476).
 * Force-pulls the encrypted server copy and replaces the local address book,
 * after confirming with the user (the restore overwrites local contacts). The
 * confirmation shows how old the server's copy is so the user knows what they'd
 * be restoring to.
 */

import { useCallback, useState } from 'react';
import { useIonAlert } from '@ionic/react';
import { useContactState } from './ContactStateContext';
import { contactsToVCards } from './util';
import { useUserContext } from '@/modules/user/useUserContext';
import { ClaimsLogic } from '@/modules/idv';
import { useAppToast } from '@/hooks/useAppToast';
import { StorageService } from '@/services/storage';
import {
  fetchServerBackup,
  decryptBackupToContacts,
  writeSyncState,
  bookSignature,
  formatBackupAge,
} from './addressBookBackup';

interface UseAddressBookRestore {
  /** Fetch the server backup, confirm, then overwrite the local book. */
  restore: () => Promise<void>;
  /** True while a restore (fetch → confirm → apply) is in flight. */
  busy: boolean;
}

export function useAddressBookRestore(): UseAddressBookRestore {
  const { dispatch } = useContactState();
  const { claims } = useUserContext();
  const [presentAlert] = useIonAlert();
  const [presentToast] = useAppToast();
  const [busy, setBusy] = useState(false);

  const restore = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      // The backup is only readable by a phone-verified session (the server
      // refuses otherwise), so surface a clear message rather than a confusing
      // network error.
      if (!ClaimsLogic.isPhoneVerified(claims)) {
        presentToast({
          message:
            'Verify your phone number to restore your address book backup.',
          duration: 4000,
          color: 'warning',
        });
        return;
      }

      const phone = await StorageService.getPhone();
      if (!phone) {
        presentToast({
          message:
            "Can't restore: this device doesn't have your phone number to unlock the backup.",
          duration: 4000,
          color: 'warning',
        });
        return;
      }

      const backup = await fetchServerBackup();
      if (!backup) {
        presentToast({
          message:
            'Could not reach the server. Check your connection and try again.',
          duration: 4000,
          color: 'danger',
        });
        return;
      }
      if (!backup.exists) {
        presentToast({
          message: 'No address book backup was found on the server yet.',
          duration: 4000,
          color: 'medium',
        });
        return;
      }

      const confirmed = await new Promise<boolean>(resolve => {
        presentAlert({
          header: 'Restore from backup?',
          message: `Are you sure you want to replace the Snowball address book stored on this device? The server's copy is ${formatBackupAge(backup.updatedAt)}.`,
          buttons: [
            { text: 'Cancel', role: 'cancel', handler: () => resolve(false) },
            {
              text: 'Restore',
              role: 'destructive',
              handler: () => resolve(true),
            },
          ],
          onDidDismiss: () => resolve(false),
        });
      });
      if (!confirmed) return;

      let contacts;
      try {
        contacts = await decryptBackupToContacts(phone, backup);
      } catch {
        presentToast({
          message: 'Could not decrypt the backup on this device.',
          duration: 4000,
          color: 'danger',
        });
        return;
      }

      dispatch({ type: 'SET_CONTACTS', payload: contacts });
      await writeSyncState({
        serverVersion: backup.version,
        serverVersionAt: backup.updatedAt ?? null,
        lastSyncedSignature: bookSignature(contactsToVCards(contacts)),
      });

      presentToast({
        message: `Restored ${contacts.length} contact${contacts.length === 1 ? '' : 's'} from the server backup.`,
        duration: 4000,
        color: 'success',
      });
    } finally {
      setBusy(false);
    }
  }, [busy, claims, dispatch, presentAlert, presentToast]);

  return { restore, busy };
}
